import { Command } from 'commander';
import inquirer from 'inquirer';
import chalk from 'chalk';
import { configManager } from '../core/config-manager.js';
import { logger } from '../utils/logger.js';
import { validateTemplateName, validateGitUrl } from '../utils/validator.js';
import { Template } from '../types/index.js';

/**
 * 模板管理命令
 */
export function registerTemplateCommand(program: Command) {
  const template = program
    .command('template')
    .description('管理项目模板');

  // template add
  template
    .command('add <name> <source>')
    .description('添加模板')
    .action(async (name: string, source: string) => {
      try {
        // 验证模板名称
        const nameValidation = validateTemplateName(name);
        if (nameValidation !== true) {
          logger.error(nameValidation as string);
          process.exit(1);
        }

        // 验证Git地址
        const urlValidation = validateGitUrl(source);
        if (urlValidation !== true) {
          logger.error(urlValidation as string);
          process.exit(1);
        }

        // 检查是否已存在
        const existingTemplate = await configManager.getTemplate(name);
        if (existingTemplate) {
          const { overwrite } = await inquirer.prompt([
            {
              type: 'confirm',
              name: 'overwrite',
              message: `模板 ${chalk.cyan(name)} 已存在，是否覆盖？`,
              default: false,
            },
          ]);

          if (!overwrite) {
            logger.info('已取消');
            return;
          }
        }

        const newTemplate: Template = {
          name,
          source,
          addedAt: new Date().toISOString(),
        };

        await configManager.addTemplate(newTemplate);
        logger.success(`已添加模板: ${chalk.cyan(name)}`);
        logger.info(`源地址: ${chalk.yellow(source)}`);
      } catch (error: any) {
        logger.error(`添加模板失败: ${error.message}`);
        process.exit(1);
      }
    });

  // template list
  template
    .command('list')
    .description('列出所有模板')
    .action(async () => {
      try {
        const templates = await configManager.getTemplates();
        const templateList = Object.values(templates);

        if (templateList.length === 0) {
          logger.warning('暂无模板，使用 "zh template add <name> <git-url>" 添加模板');
          return;
        }

        logger.title('📦 已添加的模板');

        templateList.forEach((tpl) => {
          logger.log(`  ${chalk.cyan.bold(tpl.name)}`);
          logger.log(`    ${chalk.gray(tpl.source)}`);
          logger.newLine();
        });

        logger.log(chalk.gray(`共 ${templateList.length} 个模板`));
      } catch (error: any) {
        logger.error(`获取模板列表失败: ${error.message}`);
        process.exit(1);
      }
    });

  // template remove
  template
    .command('remove <name>')
    .description('删除模板')
    .action(async (name: string) => {
      try {
        const tpl = await configManager.getTemplate(name);

        if (!tpl) {
          logger.error(`模板 ${chalk.cyan(name)} 不存在`);
          process.exit(1);
        }

        const { confirm } = await inquirer.prompt([
          {
            type: 'confirm',
            name: 'confirm',
            message: `确定要删除模板 ${chalk.cyan(name)} 吗？`,
            default: false,
          },
        ]);

        if (!confirm) {
          logger.info('已取消');
          return;
        }

        await configManager.removeTemplate(name);
        logger.success(`已删除模板: ${chalk.cyan(name)}`);
      } catch (error: any) {
        logger.error(`删除模板失败: ${error.message}`);
        process.exit(1);
      }
    });
}
